/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Button, Grid, Icon, MenuItem, Paper, Select, TextField, Typography, useTheme, alpha } from '@mui/material';
import { Environment } from '../../environment';
import { AutoCompleteParceiroListagem } from '../../forms';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Padrão visual comum aos botões da barra de ferramentas — gradientes suaves
// em vez de cor chapada, com a mesma altura/raio/sombra em todos, pra dar
// aparência de ERP moderno sem ficar chamativo. Cada botão só define seu
// próprio `background`/`color` (e, opcionalmente, um `background` de hover
// diferente) por cima disso.
const BOTAO_SX_BASE = {
  height: 40,
  borderRadius: '8px',
  paddingInline: '16px',
  fontWeight: 600,
  fontSize: '0.84rem',
  textTransform: 'none' as const,
  boxShadow: '0 2px 5px rgba(0, 30, 39, 0.08)',
  transition: 'background 160ms ease, box-shadow 160ms ease, transform 160ms ease',
};
const BOTAO_HOVER_BASE = {
  transform: 'translateY(-1px)',
  boxShadow: '0 4px 10px rgba(0, 30, 39, 0.12)',
};

interface IFerramentasDaListagemProps {
  textoDaBusca?: string;
  textoDoSelect?: string;
  textoDoStatus?: string;
  textonome?: string;
  dataInicial?: string;
  dataFinal?: string;
  mostrarInputData?: boolean;
  textSelectFalse?: string;
  textSelectTrue?: string;
  mostrarInputBusca?: boolean;
  mostrarAutoComplete?: boolean;
  disbilitarBaixar?: boolean;
  disbilitarDesconto?: boolean;
  disbilitarSalvar?: boolean;
  disbilitarCheque?: boolean;
  disbilitarImprimir?: boolean;
  aoMudarDataInicial?: (novoTexto: string) => void;
  aoMudarDataFinal?: (novoTexto: string) => void;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
  aoMudarTextoDoSelect?: (novoTextoselect: string) => void;
  aoMudarTextoDoStatus?: (novoTextoDoStatus: string) => void;
  aoMudarTextoDoParceiro?: (novoTextoselect: string, texto1: string) => void;
  // Pré-seleciona o parceiro no autocomplete de busca (ver AutoCompleteParceiroListagem.tsx)
  // — pra listagem não "esquecer" o parceiro filtrado ao voltar de outra tela.
  valorInicialParceiro?: { idparceiro: string; nome: string } | null;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  mostrarBotaoCheque?: boolean;
  // Só faz sentido na visão por parceiro (é onde dá pra escolher várias
  // contas do MESMO parceiro pra juntar, ver ListagemContasReceber.tsx).
  mostrarBotaoJuntar?: boolean;
  mostrarInputBasicSelect?: boolean
  // Opção extra no seletor de busca pra puxar só os títulos já baixados
  // dentro de um período (pela data em que o recebimento aconteceu, não
  // pelo vencimento). Opt-in porque esse componente é compartilhado com
  // Contas a Pagar, que usa a opção equivalente abaixo (mostrarOpcaoPagas).
  mostrarOpcaoRecebidas?: boolean;
  // Mesma ideia de mostrarOpcaoRecebidas, só que pro lado de Contas a
  // Pagar (filtra pela data do PAGAMENTO, ver ListagemContasPagar.tsx).
  mostrarOpcaoPagas?: boolean;
  aoClicarEmNovo?: () => void;
  aoClicarEmDesconto?: () => void;
  aoClicarEmSalvar?: () => void;
  aoClicarEmBaixa?: () => void;
  aoClicarEmCheque?: () => void;
  aoClicarEmJuntar?: () => void;
  aoClicarEmImpressao?: () => void;
}

export const FerramentasDaListagemFinanceiro: React.FC<IFerramentasDaListagemProps> = ({
  textoDaBusca = '',
  textoDoSelect = '',
  textonome='',
  dataInicial = '',
  dataFinal = '',
  aoMudarDataInicial, 
  aoMudarDataFinal,
  aoMudarTextoDeBusca,
  aoMudarTextoDoSelect,
  aoMudarTextoDoParceiro,
  valorInicialParceiro,
  mostrarInputBusca = false,
  mostrarBotaoCheque = false,
  mostrarBotaoJuntar = false,
  mostrarAutoComplete = false,
  mostrarInputBasicSelect = false,
  mostrarOpcaoRecebidas = false,
  mostrarOpcaoPagas = false,
  aoClicarEmNovo,
  aoClicarEmBaixa,
  aoClicarEmCheque,
  aoClicarEmJuntar,
  aoClicarEmImpressao,
  disbilitarBaixar,
  disbilitarCheque,
  disbilitarImprimir,
  mostrarInputData = false,
}) => {
  const theme = useTheme();
  const partesDaDataInicial = dataInicial.split('/');
  const dataInicialFormatada = partesDaDataInicial[2] + '-' + partesDaDataInicial[1] + '-' + partesDaDataInicial[0];
  const partesDaDataFinal = dataFinal.split('/');
  const dataFinalFormatada = partesDaDataFinal[2] + '-' + partesDaDataFinal[1] + '-' + partesDaDataFinal[0];

  const handleuuidparceiroNome = (nome: string | undefined) => {
    if (nome != null) {
      textonome = nome;
    }  
  };
  const handleuuidparceiro = (uuid: string | undefined) => {
    if (uuid != null) {
      aoMudarTextoDoParceiro?.(uuid, textonome);
    }  
  };
  return (
    <Box
      flexWrap="wrap"
      gap={1}
      marginX={{ xs: 1, sm: 2 }}
      padding={1} 
      paddingX={2}
      display="flex"
      alignItems="center"
      component={Paper}
      sx={{
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: theme.shadows[1],
        borderRadius: 2,
      }}
    >
      <Grid container item direction="row" spacing={1}> 
        <Grid item xs={12} sm={4} md={3} lg={2} xl={1}>
          {mostrarInputBasicSelect && (  
            <Select
              fullWidth
              size='small'
              value={textoDoSelect}
              onChange={(event) => aoMudarTextoDoSelect?.(event.target.value)}
              sx={{
                backgroundColor: alpha(theme.palette.background.default, 0.6),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                }
              }}
            >
              <MenuItem value={'r.numerodoc'}>Numero</MenuItem>
              <MenuItem value={'parceiro'}>Parceiros</MenuItem>
              <MenuItem value={'data'}>Vencimento</MenuItem>
              {mostrarOpcaoRecebidas && (
                <MenuItem value={'recebidas'}>Recebidas por período</MenuItem>
              )}
              {mostrarOpcaoPagas && (
                <MenuItem value={'pagas'}>Pagas por período</MenuItem>
              )}
            </Select>
          )} 
        </Grid>              
        {mostrarInputBusca && (
          <Grid item xs={12} sm={8} md={8} lg={8} xl={3}>  
            <TextField
              fullWidth
              size='small'
              value={textoDaBusca}
              placeholder={Environment.INPUT_DE_BUSCA}
              onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
              sx={{
                backgroundColor: alpha(theme.palette.background.default, 0.6),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                }
              }}
            />
          </Grid>  
        )} 
        {mostrarAutoComplete && (
          <Grid item xs={12} sm={8} md={8} lg={4} xl={4}>                  
            <AutoCompleteParceiroListagem
              nome={handleuuidparceiroNome}
              uuidparceiro={handleuuidparceiro}
              isExternalLoading={false}
              valorInicial={valorInicialParceiro}
            />
          </Grid>
        )} 
        {mostrarInputData && (
         <Grid item  xs={12} sm={12} md={7} lg={5} xl={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{
                  minWidth: 150,
                  marginLeft:1,
                  maxWidth: 180,
                  backgroundColor: alpha(theme.palette.background.default, 0.6),
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                }}
                label="Data Inicial" 
                format='DD/MM/YYYY'
                defaultValue={dayjs()}
                value={dayjs(dataInicialFormatada)}
                onChange={(date: Dayjs | null) => {
                  const novoTexto = date ? date.format('DD/MM/YYYY') : '';
                  aoMudarDataInicial?.(novoTexto);
                }}
                slotProps={{
                  textField: {    
                    size:'small',
                  }
                }}
              />
              <DatePicker
                sx={{
                  minWidth: 150,
                  marginLeft:1,
                  maxWidth: 180,
                  backgroundColor: alpha(theme.palette.background.default, 0.6),
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                }}
                label="Data Final" 
                format='DD/MM/YYYY'
                defaultValue={dayjs()}
                value={dayjs(dataFinalFormatada)}
                onChange={(date: Dayjs | null) => {
                  const novoTexto = date ? date.format('DD/MM/YYYY') : '';
                  aoMudarDataFinal?.(novoTexto);
                }}
                slotProps={{
                  textField: {    
                    size:'small',
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        )}  
        <Grid item  xs={6} sm={6} md={2} lg={1.5} xl={1}>
          <Button
            fullWidth
            size="small"
            variant='contained'
            disableElevation
            onClick={aoClicarEmNovo}
            startIcon={<Icon>add</Icon>}
            sx={{
              ...BOTAO_SX_BASE,
              background: 'linear-gradient(135deg, #00cfe4 0%, #00b8d0 100%)',
              color: '#001e27',
              '&:hover': {
                ...BOTAO_HOVER_BASE,
                background: 'linear-gradient(135deg, #00dcef 0%, #00c3d8 100%)',
              }
            }}
          >
            Adicionar
          </Button>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={1.5} xl={1}>
          <Button
            fullWidth
            size="small"
            variant='contained'
            disabled={disbilitarBaixar}
            disableElevation
            onClick={aoClicarEmBaixa}
            startIcon={<Icon>paid</Icon>}
            sx={{
              ...BOTAO_SX_BASE,
              background: 'linear-gradient(135deg, #13b88a 0%, #07956f 100%)',
              color: '#ffffff',
              '&:hover': BOTAO_HOVER_BASE,
            }}
          >
            Baixar
          </Button>
        </Grid>
        {mostrarBotaoCheque && (
          <Grid item xs={6} sm={6} md={2} lg={1.5} xl={1}>
            <Button
              fullWidth
              size="small"
              variant='contained'
              disableElevation
              disabled={disbilitarCheque}
              onClick={aoClicarEmCheque}
              startIcon={<Icon>payment</Icon>}
              sx={{
                ...BOTAO_SX_BASE,
                background: 'linear-gradient(135deg, #4776e6 0%, #3159cf 100%)',
                color: '#ffffff',
                '&:hover': BOTAO_HOVER_BASE,
              }}
            >
               Cheque
            </Button>
          </Grid>
        )}
        {mostrarBotaoJuntar && (
          <Grid item xs={6} sm={6} md={2} lg={1.5} xl={1}>
            <Button
              fullWidth
              size="small"
              variant='contained'
              disableElevation
              onClick={aoClicarEmJuntar}
              startIcon={<Icon>call_merge</Icon>}
              sx={{
                ...BOTAO_SX_BASE,
                background: 'linear-gradient(135deg, #18bfd2 0%, #079caf 100%)',
                color: '#ffffff',
                '&:hover': BOTAO_HOVER_BASE,
              }}
            >
              Juntar
            </Button>
          </Grid>
        )}
        <Grid item xs={6} sm={6} md={2} lg={1.5} xl={1}>
          <Button
            fullWidth
            size="small"
            variant='outlined'
            disableElevation
            disabled={disbilitarImprimir}
            onClick={aoClicarEmImpressao}
            startIcon={<Icon>print</Icon>}
            sx={{
              ...BOTAO_SX_BASE,
              background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha('#009eb5', 0.06)} 100%)`,
              border: `1px solid ${alpha('#009eb5', 0.35)}`,
              color: theme.palette.mode === 'dark' ? '#4dd8ec' : '#009eb5',
              '&:hover': {
                ...BOTAO_HOVER_BASE,
                borderColor: '#009eb5',
              }
            }}
          >
             Imprimir
          </Button>
        </Grid>
      </Grid> 
    </Box>
  );
};